import React from 'react'
import { Box, VStack, HStack, Text, Badge, Checkbox, WrapItem } from '@chakra-ui/react'
import { FiTrendingUp, FiFileText } from 'react-icons/fi'
import { TagRecommendation } from '@/types'

interface TagSuggestionCardProps {
  recommendation: TagRecommendation
  isSelected: boolean
  onToggle: (tagName: string) => void
}

export function TagSuggestionCard({
  recommendation,
  isSelected,
  onToggle,
}: TagSuggestionCardProps) {
  const confidence = recommendation.score * 100

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'green'
    if (score >= 0.6) return 'orange'
    if (score >= 0.4) return 'yellow'
    return 'gray'
  }

  const getConfidenceLabel = (score: number) => {
    if (score >= 0.8) return 'High'
    if (score >= 0.6) return 'Medium'
    return 'Low'
  }

  return (
    <WrapItem>
      <Box
        p={4}
        border="1px solid"
        borderColor={isSelected ? 'blue.300' : 'gray.200'}
        borderRadius="lg"
        bg={isSelected ? 'blue.50' : 'white'}
        cursor="pointer"
        onClick={() => onToggle(recommendation.tag_name)}
        _hover={{
          borderColor: isSelected ? 'blue.400' : 'blue.200',
          bg: isSelected ? 'blue.100' : 'blue.25',
          transform: 'translateY(-1px)',
          boxShadow: 'md',
        }}
        transition="all 0.2s ease"
        boxShadow={isSelected ? 'sm' : 'xs'}
        minW="200px"
      >
        <VStack spacing={3} align="stretch">
          <HStack spacing={3} justify="space-between">
            <Checkbox
              isChecked={isSelected}
              onChange={() => onToggle(recommendation.tag_name)}
              onClick={(e) => e.stopPropagation()}
              colorScheme="blue"
              size="lg"
            />
            <Badge
              colorScheme={getScoreColor(recommendation.score)}
              variant="solid"
              borderRadius="full"
              px={2}
              py={1}
              fontSize="xs"
              fontWeight="bold"
            >
              {Math.min(confidence, 100).toFixed(0)}%
            </Badge>
          </HStack>

          <VStack spacing={1} align="start">
            <Text fontWeight="semibold" fontSize="md" color="gray.800">
              {recommendation.tag_name}
            </Text>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {getConfidenceLabel(recommendation.score)} Confidence
            </Text>
          </VStack>

          <VStack spacing={1} align="start">
            {recommendation.similar_files_count > 0 && (
              <HStack spacing={1}>
                <FiTrendingUp size="12px" color="gray" />
                <Text fontSize="xs" color="gray.500">
                  {recommendation.similar_files_count} similar files
                </Text>
              </HStack>
            )}
            {recommendation.sources?.includes('content_analysis') && (
              <HStack spacing={1}>
                <FiFileText size="12px" color="green" />
                <Text fontSize="xs" color="green.600">
                  Content analysis
                </Text>
              </HStack>
            )}
          </VStack>
        </VStack>
      </Box>
    </WrapItem>
  )
}
